import { Controller } from "@hotwired/stimulus"
import { searchMarketplace } from "../lib/plant_search"
import L from "leaflet"

// Leaflet map with server-side geo search for marketplace items
// Values: bbox (Array), query, searchParams (Object)
// Targets: map, searchOnMove
export default class extends Controller {
  static values = { bbox: Array, query: String, searchParams: Object }
  static targets = ["map", "searchOnMove"]

  connect() {
    this.searchOnMove = true
    this.markers = null
    this.disconnected = false
    this.searchAbort = null

    this.initMap()
    this.search()

    this.handleMove = this.handleMove.bind(this)
    this.mymap.on("moveend", this.handleMove)

    document.addEventListener("turbo:load", () => {
      if (window._turboScroll) {
        document.scrollingElement.scrollTo(0, window._turboScroll)
      }
      window._turboScroll = 0
    })
  }

  disconnect() {
    this.disconnected = true

    if (this.searchAbort) {
      this.searchAbort.abort()
    }

    if (this.mymap) {
      this.mymap.off("moveend", this.handleMove)
      this.mymap.remove()
    }
  }

  initMap() {
    const bbox = this.bboxValue

    if (bbox && bbox.length >= 5) {
      this.mymap = L.map(this.mapTarget).fitBounds([
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]]
      ]).setZoom(bbox[4])
    } else {
      this.mymap = L.map(this.mapTarget).setView([30, -10], 2)
    }

    L.tileLayer("https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoicGVybWFwZW9wbGUiLCJhIjoiY2xndjg5empjMDE3bDNrcGxqazN4eWQxYiJ9.TTzaA931zw2NhANr-tXuuA", {
      minZoom: 2,
      maxZoom: 18,
      attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>',
      id: "mapbox/streets-v11",
      tileSize: 512,
      zoomOffset: -1
    }).addTo(this.mymap)

    this.markers = L.layerGroup()
    this.mymap.addLayer(this.markers)
  }

  toggleSearchOnMove(event) {
    this.searchOnMove = event.target.checked
  }

  // Called when the mobile filters drawer is revealed. Leaflet needs a size
  // recalculation because the map was initialised while its container was hidden.
  resize() {
    if (this.mymap) {
      // Defer to the next frame so the drawer's slide-in transition has laid out.
      requestAnimationFrame(() => this.mymap.invalidateSize())
    }
  }

  handleMove() {
    const bounds = this.mymap.getBounds()
    const zoom = this.mymap.getZoom()
    const bbox = [
      bounds.getNorthWest().lat,
      bounds.getNorthWest().lng,
      bounds.getSouthEast().lat,
      bounds.getSouthEast().lng,
      zoom
    ]

    this.bboxValue = bbox

    if (this.searchOnMove) {
      const url = new URL(window.location.href)
      url.searchParams.set("bbox", bbox.join(","))

      window._turboScroll = document.scrollingElement.scrollTop
      Turbo.visit(url.toString())
      this.search()
    }
  }

  search() {
    const bbox = this.bboxValue
    const bboxStr = bbox && bbox.length >= 4 ? bbox.join(",") : undefined

    if (this.searchAbort) {
      this.searchAbort.abort()
    }
    this.searchAbort = new AbortController()

    return searchMarketplace({ q: this.queryValue || "", bbox: bboxStr }, this.searchAbort.signal)
      .then((result) => {
        this.updateMap(result?.hits || [])
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        // A timeout, network blip, or unexpected response shape shouldn't leave
        // the page in a broken state — just clear the markers and move on.
        console.warn("Marketplace map search failed", error)
        this.updateMap([])
      })
  }

  updateMap(items) {
    // A search may resolve after Turbo tears the map down on navigation.
    if (this.disconnected) return

    this.markers.clearLayers()
    if (!Array.isArray(items)) return
    items.forEach(item => {
      if (item._geo) {
        const seller = item.seller || ""
        this.markers.addLayer(
          L.marker([item._geo.lat, item._geo.lng])
            .bindPopup(
              "<div class='map-popup'><div class='map-popup-content'><label class='map-popup-title'>" +
              this.escapeHtml(seller) +
              "</label></div><a class='map-popup-btn' href='/marketplace?seller=" +
              encodeURIComponent(seller) +
              "'>View seller</a></div>"
            )
        )
      }
    })
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
